import type { Question } from "./data"
import { backHome } from "./quizState"
import { rerender } from "./render"
import { SectionPicker } from "./SectionPicker"
import { getHistory, isBookmarked, toggleBookmark } from "./storage"
import { activeQuestions, bankCoverage, isBankGateMet } from "./studySignals"

const RECENT_MISS_LIMIT = 5

function recentMisses(limit: number): Array<{ q: Question; at: number }> {
  const items: Array<{ q: Question; at: number }> = []
  for (const q of activeQuestions()) {
    const events = getHistory(q)
    let lastWrongAt = 0
    for (const e of events) {
      if (!e.correct && e.at > lastWrongAt) lastWrongAt = e.at
    }
    if (lastWrongAt > 0) items.push({ q, at: lastWrongAt })
  }
  items.sort((a, b) => b.at - a.at)
  return items.slice(0, limit)
}

function bookmarkedQuestions(): Question[] {
  return activeQuestions()
    .filter(isBookmarked)
    .sort((a, b) => {
      if (a.answer.section !== b.answer.section) return a.answer.section - b.answer.section
      return a.answer.page - b.answer.page
    })
}

export function Explore() {
  const misses = recentMisses(RECENT_MISS_LIMIT)
  const bookmarks = bookmarkedQuestions()
  const bank = bankCoverage()
  const bankPct = bank.total === 0 ? 0 : Math.round((bank.seen / bank.total) * 100)
  const bankGateMet = isBankGateMet()

  return (
    <>
      <div class="explore-backrow">
        <button type="button" class="btn ghost" onClick={backHome}>
          ← Home
        </button>
      </div>
      <div class="explore-title-block">
        <div class="explore-kicker">Track your progress</div>
        <h2 class="explore-title">Study Guide</h2>
      </div>

      <div class="screen-section">
        <h3 class="section-heading">Progress</h3>
        <div class="explore-bank">
          {bank.seen} of {bank.total} questions seen ({bankPct}%)
        </div>
        {!bankGateMet && <div class="explore-coverage-note">Practice more to surface section-level signals.</div>}
      </div>

      {bookmarks.length > 0 && (
        <div class="screen-section">
          <h3 class="section-heading">Bookmarks</h3>
          <div class="explore-list">
            {bookmarks.map((q) => (
              <QuestionRow key={q.id} q={q} bookmark />
            ))}
          </div>
        </div>
      )}

      {misses.length > 0 && (
        <div class="screen-section">
          <h3 class="section-heading">Recently Missed</h3>
          <div class="explore-list">
            {misses.map(({ q }) => (
              <QuestionRow key={q.id} q={q} />
            ))}
          </div>
        </div>
      )}

      <div class="screen-section">
        <h3 class="section-heading">Per-section practice</h3>
        <SectionPicker />
      </div>
    </>
  )
}

function QuestionRow({ q, bookmark }: { q: Question; bookmark?: boolean }) {
  return (
    <div class="explore-row">
      <div class="explore-row-q">{q.q}</div>
      <div class="explore-row-a">{q.answer.text}</div>
      <div class="explore-row-footer">
        <span class="explore-row-meta">
          Section {q.answer.section} · Page {q.answer.page}
        </span>
        {bookmark && (
          <button
            type="button"
            class="bookmark-btn active"
            onClick={() => {
              toggleBookmark(q)
              rerender()
            }}
          >
            Bookmarked
          </button>
        )}
      </div>
    </div>
  )
}
